import { Mail, Phone, Video, FileText, MessageSquare, Clock, Activity } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface TimelineItem {
  id: string;
  type: string;
  description: string;
  entityName?: string;
  userName?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

interface RecordTimelineProps {
  activities: TimelineItem[];
  className?: string;
}

const iconMap: Record<string, typeof Mail> = {
  email: Mail,
  phone: Phone,
  meeting: Video,
  note: FileText,
  message: MessageSquare,
  activity: Activity,
};

const colorMap: Record<string, string> = {
  email: 'bg-blue-100 text-blue-600',
  phone: 'bg-green-100 text-green-600',
  meeting: 'bg-purple-100 text-purple-600',
  note: 'bg-yellow-100 text-yellow-600',
  message: 'bg-pink-100 text-pink-600',
  activity: 'bg-slate-100 text-slate-600',
};

function groupByDate(items: TimelineItem[]): { label: string; items: TimelineItem[] }[] {
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString();

  const groups: Record<string, TimelineItem[]> = {};
  const groupOrder: string[] = [];

  for (const item of items) {
    const date = new Date(item.createdAt);
    const dateStr = date.toDateString();
    let label: string;

    if (dateStr === today) {
      label = 'Today';
    } else if (dateStr === yesterday) {
      label = 'Yesterday';
    } else {
      const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
      if (diffDays <= 7) {
        label = 'This Week';
      } else {
        label = 'Earlier';
      }
    }

    if (!groups[label]) {
      groups[label] = [];
      groupOrder.push(label);
    }
    groups[label].push(item);
  }

  return groupOrder.map((label) => ({ label, items: groups[label] }));
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function RecordTimeline({ activities, className }: RecordTimelineProps) {
  if (activities.length === 0) {
    return (
      <div className={cn('py-8 text-center text-sm text-slate-400', className)}>
        No activity yet.
      </div>
    );
  }

  const groups = groupByDate(activities);

  return (
    <div className={cn('space-y-6', className)}>
      {groups.map((group) => (
        <div key={group.label}>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            {group.label}
          </h4>
          <div className="relative space-y-0">
            {/* Vertical line */}
            <div className="absolute left-4 top-2 bottom-2 w-px bg-surface-200" />

            {group.items.map((item) => {
              const Icon = iconMap[item.type] ?? Clock;
              const iconColor = colorMap[item.type] ?? colorMap.activity;

              return (
                <div key={item.id} className="relative flex gap-3 py-2">
                  <div
                    className={cn(
                      'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      iconColor,
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-sm text-slate-700">{item.description}</p>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                      {item.userName && <span>{item.userName}</span>}
                      <span>{formatTime(item.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
